import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLoadingToast } from '../../resources';
import { useConfirmCreateShareDialog } from '../hooks/useConfirmCreateShareDialog';
import { useConfirmUploadLinkShareDialog } from '../hooks/useConfirmUploadLinkShareDialog';
import TEXT from './generated/TEXT/ShareActionsContext';

type Props = PropsWithChildren<{
  checkShareRisks: (assetIds: string[]) => Promise<{
    hasRestrictedFiles: boolean;
    hasRestrictedFolders: boolean;
    hasUploadLinkFolders: boolean;
  }>;
}>;

type ShareActionsContext = {
  checkCreateShareRestrictedAssets: (
    assetIds: string[] | undefined
  ) => Promise<{
    hasRestrictedAssets: boolean;
    hasUploadLinkFolders: boolean;
    confirmed: boolean;
  }>;
};

TEXT.DEFINE({
  /** Loading toast message shown while checking for restricted assets */
  checkingRestrictedAssets: 'Preparing...',
});
/**
 * Context for managing share-related actions.
 *
 * This context provides methods for validating and handling share creation,
 * particularly when dealing with potentially restricted assets.
 *
 * The default behavior of the `checkCreateShareRestrictedAccess` is to confirm
 * and continue the create share flow. This allows existing uses to remain
 * unchanged.
 */
export const ShareActionsContext = createContext<ShareActionsContext>({
  checkCreateShareRestrictedAssets: () =>
    Promise.resolve({
      hasRestrictedAssets: false,
      hasUploadLinkFolders: false,
      confirmed: true,
    }),
});

/**
 * Hook to access the ShareActionsContext.
 */
export const useShareActionsContext = () => {
  return useContext(ShareActionsContext);
};

/**
 * Provider component for the ShareActionsContext.
 *
 * This provider supplies functionality for checking and handling restricted
 * assets when creating shares. It includes confirmation dialog handling for
 * cases where restricted assets are detected.
 *
 * @param props - Standard React props with children
 * @returns A context provider that wraps the provided children
 */
export const ShareActionsContextProvider = ({
  children,
  checkShareRisks,
}: Props) => {
  const { confirmCreateShareDialog, getConfirmCreateShareResult } =
    useConfirmCreateShareDialog();
  const { confirmUploadLinkShareDialog, getConfirmUploadLinkShareResult } =
    useConfirmUploadLinkShareDialog();

  const { showToast, hideToast } = useDelayedLoadingToast({
    title: TEXT.checkingRestrictedAssets,
    delay: TOAST_DELAY,
  });

  // Checks the assets being shared for share-risk conditions (restricted items
  // and/or folders with active upload links) and shows the matching
  // confirmation dialog for each. Returns `confirmed: false` if the user
  // cancels any dialog, so callers can abort the share.
  const checkCreateShareRestrictedAssets = useCallback(
    async (assetIds: string[] | undefined) => {
      if (!Array.isArray(assetIds) || assetIds.length === 0) {
        return {
          hasRestrictedAssets: false,
          hasUploadLinkFolders: false,
          confirmed: true,
        };
      }

      showToast();
      const { hasRestrictedFiles, hasRestrictedFolders, hasUploadLinkFolders } =
        await checkShareRisks(assetIds);
      hideToast();

      const hasRestrictedAssets = hasRestrictedFiles || hasRestrictedFolders;

      if (hasRestrictedAssets) {
        const confirmed = await getConfirmCreateShareResult();
        if (!confirmed) {
          return { hasRestrictedAssets, hasUploadLinkFolders, confirmed };
        }
      }

      if (hasUploadLinkFolders) {
        const confirmed = await getConfirmUploadLinkShareResult();
        if (!confirmed) {
          return { hasRestrictedAssets, hasUploadLinkFolders, confirmed };
        }
      }

      return { hasRestrictedAssets, hasUploadLinkFolders, confirmed: true };
    },
    [
      showToast,
      checkShareRisks,
      hideToast,
      getConfirmCreateShareResult,
      getConfirmUploadLinkShareResult,
    ]
  );

  const ctx = useMemo(
    () => ({
      checkCreateShareRestrictedAssets,
    }),
    [checkCreateShareRestrictedAssets]
  );

  return (
    <ShareActionsContext.Provider value={ctx}>
      {children}
      {confirmCreateShareDialog}
      {confirmUploadLinkShareDialog}
    </ShareActionsContext.Provider>
  );
};

/**
 * Hook that manages a loading toast with a configurable delay before showing.
 *
 * This hook prevents showing loading toasts for operations that complete quickly,
 * improving the user experience by reducing UI flicker for fast operations.
 *
 * @param options - Configuration options for the delayed toast
 * @param options.title - The text to display in the toast
 * @param options.delay - Time in milliseconds to wait before showing the toast
 * @returns Object containing functions to control the toast
 * @returns {Function} showToast - Starts the delay timer to show the toast
 * @returns {Function} hideToast - Cancels the timer and hides any visible toast
 */
const useDelayedLoadingToast = ({
  title,
  delay,
}: {
  title: string;
  delay: number;
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addToast, updateAndHideToast } = useLoadingToast();
  const [loadingToast, setLoadingToast] = useState<string | null>(null);
  const [showLoadingToast, setShowLoadingToast] = useState<boolean>(false);

  // manage showing & hiding of the toast
  useEffect(() => {
    if (showLoadingToast && !loadingToast) {
      setLoadingToast(addToast(TEXT.checkingRestrictedAssets));
    }
    if (!showLoadingToast && loadingToast) {
      updateAndHideToast(loadingToast, { title });
      setLoadingToast(null);
    }
  }, [addToast, loadingToast, showLoadingToast, title, updateAndHideToast]);

  // trigger a delayed toast to appear
  const showToast = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setShowLoadingToast(true), delay);
  };

  // cancel the delayed toast and hide any active ones
  const hideToast = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = null;
    setShowLoadingToast(false);
  };

  return {
    showToast,
    hideToast,
  };
};

const TOAST_DELAY = 500;
